import React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {FormField} from 'shells/form/form-field';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import type {
    GetVerifiedEmailAddresses_fromAddresses_edges_node as EmailAddress,
    GetSessionUser_loggedInUser,
} from 'nutshell-graphql-types';

import {GraphQLSelectMarketingEmails} from '../../../core/select/graphql-select-marketing-emails';

type User = {
    id: string;
    name: string;
};

type Props = {
    loggedInUser: GetSessionUser_loggedInUser | null | undefined;
    owner: User | null | undefined;
    fromEmailAddress: EmailAddress | null | undefined;
    onFromEmailAddressChange: (emailAddress: EmailAddress) => void;
};

export function FromField(props: Props) {
    const [numberOfEmailAddresses, setNumberOfEmailAddresses] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const helperText = getHelperText(
        props.loggedInUser,
        props.owner,
        props.fromEmailAddress,
        numberOfEmailAddresses,
        isLoading
    );

    return (
        <FormField
            tooltipInfo={
                <div>
                    This will appear to your recipients as the “from” name and email address.
                    {props.loggedInUser && props.loggedInUser.permissions.canAccessSetup && (
                        <>
                            {' '}
                            Admins can manage these email addresses in the{' '}
                            <Link
                                to='/email-marketing/sender-address'
                                // @ts-expect-error
                                as={RoutedLink}
                                size='small'
                                variant='primary'
                            >
                                Sender address settings.
                            </Link>
                        </>
                    )}
                </div>
            }
            innerId='from'
            label='From'
            helperText={helperText}
        >
            <GraphQLSelectMarketingEmails
                userId={(props.owner && props.owner.id) || undefined}
                selectedId={props.fromEmailAddress ? props.fromEmailAddress.id : null}
                onChange={(emailAddress: EmailAddress) => {
                    props.onFromEmailAddressChange(emailAddress);
                }}
                autoFocus={false}
                onNumberOfEmailAddressesChange={setNumberOfEmailAddresses}
                onIsLoadingChange={setIsLoading}
            />
        </FormField>
    );
}

function getHelperText(
    loggedInUser: GetSessionUser_loggedInUser | null | undefined,
    owner: User | null | undefined,
    fromEmailAddress: EmailAddress | null | undefined,
    numberOfEmailAddresses: number,
    isLoadingEmails?: boolean
) {
    if (isLoadingEmails) {
        return null;
    }

    if (fromEmailAddress && fromEmailAddress.status !== 'VERIFIED') {
        return (
            <>
                <Body color='warn' size='small'>
                    This email address is not verified yet.
                </Body>
                {loggedInUser && loggedInUser.permissions.canAccessSetup && (
                    <>
                        {' '}
                        <Link
                            to='/email-marketing/sender-address'
                            // @ts-expect-error
                            as={RoutedLink}
                            size='small'
                            variant='primary'
                        >
                            Manage senders
                        </Link>
                    </>
                )}
            </>
        );
    }

    const loggedInUserIsOwner = owner && loggedInUser && owner.id === loggedInUser.id;
    if (numberOfEmailAddresses === 0) {
        return (
            <>
                {(!owner || loggedInUserIsOwner) && (
                    <Body size='small' color='warn'>
                        You have no verified email addresses.
                    </Body>
                )}
            </>
        );
    }

    return null;
}
